/**
 * WEB-2552 — разбор содержимого редактора и вторая дверь потолка вариантов ответа, вынесены из editorSlice.ts
 * (пачка 49, заход Р1; «перенос токен в токен»): normalizePlanSelectorBlock/parseContent/
 * replaceContentState и withOptionCapEnforced. `isNotFound` в этот файл не
 * переехал — он нужен только санкам загрузки и лежит рядом с ними
 * (`./thunks/load.ts`).
 */
import type { EditorBlock, PlanSelectorPlan } from "./types/blocks";
import type { EditorScreen } from "./types/screen";
import type { EditorState } from "./state";
// WEB-1532: потолок вариантов ответа — зеркало серверной схемы
// (block-schema/questions.ts). Держим и здесь, а не только на кнопке панели:
// addBlockOption зовут не только из панели свойств.
import { BLOCK_LIST_FIELD_LIMITS_BY_TYPE } from "@/constants/blockOptionLimits";
// М2 (ревизия волны хвостов-8): отказ второй двери потолка обязан отменять
// ОБЕ половины поля с двойным именем — реестр пар и здесь единственный
// источник знания, какое имя парное.
import { generationFieldName } from "@/components/editor/blockFieldNamePairs";
import { pickStrings } from "@/i18n";
import { editorStrings } from "@/i18n/dictionaries/editor";

/**
 * WEB-1532 (ревизия пачек 7-8, находка 1) — ВТОРАЯ ДВЕРЬ ДЛЯ ВСЕХ ПЯТИ
 * СЕМЕЙСТВ ВАРИАНТОВ, а не только для тех, что ходят через `addBlockOption`.
 *
 * Из правки убирается поле-список вариантов, если правка ПОДНИМАЕТ его длину
 * выше потолка. Уменьшение проходит всегда — даже когда список уже перебирает
 * потолок: старую воронку иначе не починить руками (шаг 4 сценария приёмки —
 * «удалить лишний вариант»).
 *
 * Тихо обрезать список нельзя: в вариантах лежат авторский текст и
 * `nextScreenId` — ветвление воронки. Молчаливая порча хуже честного отказа
 * (приём WEB-1496 «выбросить негодное поле» здесь сознательно не применяется).
 *
 * Какое поле у какого типа блока — берётся из общего реестра, а не из знания
 * этого файла: список имён живёт в `constants/blockOptionLimits.ts` под
 * сторожем, сверяющим их с серверной схемой через реестр пар имён.
 */
export function withOptionCapEnforced<T extends Record<string, unknown>>(
  block: EditorBlock | undefined,
  updates: T,
): T {
  if (!block) return updates;
  // WEB-1687: полей-списков у одного типа блока может быть несколько, и
  // потолок у каждого свой (у экрана загрузки — шаги 20, вопросы 20, строки
  // отзывов 30). Раньше здесь читалось ОДНО поле на тип, поэтому вторая дверь
  // у таких блоков стерегла в лучшем случае одно из трёх.
  const limits = BLOCK_LIST_FIELD_LIMITS_BY_TYPE[block.type];
  if (!limits) return updates;

  // `rejectedAny` отдельным флагом, а не «result !== null»: без него вывод
  // типов зацикливается (result получает тип из rest, а rest — из result).
  let rejectedAny = false;
  let result: Record<string, unknown> = updates;
  for (const { field, max } of limits) {
    const source: Record<string, unknown> = result;
    if (!(field in source)) continue;

    const next = source[field];
    if (!Array.isArray(next)) continue;
    const current = (block as unknown as Record<string, unknown>)[field];
    const currentLength = Array.isArray(current) ? current.length : 0;
    // Отклоняется только РОСТ выше потолка: уменьшение разрешено всегда,
    // иначе старую воронку с перебором нельзя было бы починить руками.
    if (next.length <= max || next.length <= currentLength) continue;

    // М1 (ревизия волны хвостов-8): отказ больше не немой. Хранилищу нельзя
    // тащить зависимости интерфейса (antd `message` в слайсе), а поле `error`
    // состояния редактора в интерфейсе ничем не читается — значит доступный
    // здесь честный сигнал ровно один: предупреждение в консоли, называющее
    // блок, поле, потолок и запрошенную длину. Первая дверь (заглушённая
    // кнопка с подсказкой в панели) остаётся основным разговором с человеком.
    console.warn(
      pickStrings(editorStrings).optionLimitRejected(
        block.type,
        field,
        next.length,
        max,
      ),
    );

    const { [field]: _rejected, ...rest } = source;
    result = rest as Record<string, unknown>;
    rejectedAny = true;

    // М2: отказ обязан быть АТОМАРНЫМ. Правка панели приходит сюда уже
    // дополненной гашением ПАРНОГО имени генерации (фикс F5,
    // `withPairedGenerationNamesCleared`) — у поля две номенклатуры. Выбросив
    // одно имя и оставив `<парное>: undefined`, дверь стирала бы тем же ходом
    // тот самый список, перезаписать который она только что отказалась.
    // WEB-1687: у новых полей пара ГАСИМАЯ (`loaderSteps` ↔ `steps`) — до сих
    // пор капнутое поле с гасимой парой было только гипотезой структурного
    // сторожа, теперь это рабочий случай.
    const paired = generationFieldName(block.type, field);
    if (paired && paired !== field && paired in result) {
      const withoutPair: Record<string, unknown> = { ...result };
      delete withoutPair[paired];
      result = withoutPair;
    }
  }

  return rejectedAny ? (result as unknown as T) : updates;
}

/**
 * WEB-625: plan_selector carries a dual data model — the canonical
 * `planSelector*` fields (written by the properties panel) and AI-shaped short
 * fields (`plans/layout/defaultPriceId`, produced by AI generation and used as a
 * render fallback in PublicBlockRendererExtended). The panel only reads the
 * canonical fields, so an AI-generated block rendered N plans on the canvas while
 * the panel showed none — a plan-count/layout desync. Normalize the AI-shaped
 * fields into the canonical ones on load so panel and canvas share one source of
 * truth. Canonical values always win; only absent ones are backfilled.
 */
interface AiShapedPlanSelector {
  plans?: PlanSelectorPlan[];
  layout?: "cards" | "list";
  defaultPriceId?: string;
}

function normalizePlanSelectorBlock(block: EditorBlock): EditorBlock {
  if (block.type !== "plan_selector") return block;
  const ai = block as EditorBlock & AiShapedPlanSelector;
  const planSelectorPlans = block.planSelectorPlans ?? ai.plans;
  const planSelectorLayout = block.planSelectorLayout ?? ai.layout;
  const planSelectorDefaultPriceId =
    block.planSelectorDefaultPriceId ?? ai.defaultPriceId;

  if (
    planSelectorPlans === block.planSelectorPlans &&
    planSelectorLayout === block.planSelectorLayout &&
    planSelectorDefaultPriceId === block.planSelectorDefaultPriceId
  ) {
    return block;
  }

  return {
    ...block,
    ...(planSelectorPlans !== undefined ? { planSelectorPlans } : {}),
    ...(planSelectorLayout !== undefined ? { planSelectorLayout } : {}),
    ...(planSelectorDefaultPriceId !== undefined
      ? { planSelectorDefaultPriceId }
      : {}),
  };
}

export function parseContent(raw: unknown): EditorScreen[] {
  if (!Array.isArray(raw)) return [];
  return (raw as EditorScreen[]).map((screen) =>
    Array.isArray(screen?.blocks)
      ? { ...screen, blocks: screen.blocks.map(normalizePlanSelectorBlock) }
      : screen,
  );
}

/**
 * Тело `replaceContent`, вынесенное отдельной функцией: тот же самый разбор
 * содержимого и сохранение выбора нужны и одношаговому применению хода
 * (`applyTurnResult`, WEB-2382 п.3). Второй копии правила «что делать с
 * выбранным экраном/блоком после подмены содержимого» быть не должно.
 */
export function replaceContentState(
  state: EditorState,
  payload: EditorScreen[],
): EditorState {
  // WEB-625: the AI-chat apply path (ThreePanelEditor → replaceContent)
  // bypasses parseContent, so AI-shaped plan_selector blocks kept the dual
  // data model and the panel↔canvas plan-count desync survived. Normalize
  // here too so both read the canonical planSelector* fields.
  const content = parseContent(payload);
  // WEB-952: ход ИИ-чата применяется через replaceContent, а безусловный
  // `content[0].id` выкидывал пользователя на первый экран после КАЖДОГО
  // хода. Держим выбор, если экран пережил ход; блок — только если он всё
  // ещё лежит внутри этого же экрана. Экран пропал → фолбэк на первый.
  // (Сброс в loadQuizForEditor/loadPaywallForEditor не трогаем — там это
  // корректно: открывается другой ресурс.)
  const keptScreen = content.find((s) => s.id === state.selectedScreenId);
  const selectedScreenId = keptScreen?.id ?? content[0]?.id ?? null;
  const keptBlock =
    keptScreen && state.selectedBlockId
      ? keptScreen.blocks?.some((b) => b.id === state.selectedBlockId)
      : false;
  return {
    ...state,
    content,
    selectedScreenId,
    selectedBlockId: keptBlock ? state.selectedBlockId : null,
    isDirty: true,
  };
}
